import { decrypt } from '@/common/securityUtils';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { Label } from '@/design-system/label';
import { useAttributes } from '@/hooks/useAttributes';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { GalleryIcon } from '@/icons';
import { useMemo } from 'react';
import { usePremiumStorePreview } from './hooks/usePremiumStorePreview';
import { INDENTIFICATION_TYPES } from './identificationType';
import { StorePreviewSkeleton } from './skeletons/FullStorePreviewSkeleton';

type Attr = { value: string; labelEng: string; label: string };

const StorePreview = ({ clubId, holdId }: { clubId: string; holdId: string }) => {
  const { getPendingPremiumStoreDetails, isLoading } = usePremiumStorePreview(clubId, holdId);
  const { data: identificationTypeOptions = [] } = useAttributes('identificationType') ?? {};
  const { locale } = useDynamicLocale();
  const { onOpen } = useDialogFormStore();

  const details = getPendingPremiumStoreDetails;

  const identificationType = useMemo(() => {
    return decrypt(details?.identificationType || '');
  }, [details?.identificationType]);

  const getLabelFromAttributes = (value: string | number | undefined, attributes: Attr[]) => {
    if (value === undefined || value === null) return 'N/A';
    const v = String(value);
    const found = attributes.find((item) => String(item.value) === v);
    if (!found) return 'N/A';
    return locale === 'en' ? found.labelEng : found.label;
  };

  const getBusinessType = (businessType?: string) => {
    if (!businessType) return 'N/A';
    if (businessType === '1') return locale === 'en' ? 'Corporation' : '法人';
    return locale === 'en' ? 'Sole Proprietorship' : '個人事業主';
  };

  const downloadFromUrl = async (url: string, fileName: string) => {
    if (!url) return;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName; // you can pass "KYC1.jpg" etc if you want extension
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(objectUrl);
    } catch {
      // optional: toast error
      // toast.error('Download failed');
    }
  };

  // Compute KYC urls once so View and Download are always consistent
  const kyc1Url =
    identificationType === INDENTIFICATION_TYPES.PASSPORT_INSURANCE
      ? details?.passportImage?.path
      : details?.kycFrontImage?.path;

  const kyc2Url =
    identificationType === INDENTIFICATION_TYPES.PASSPORT_INSURANCE
      ? details?.insuranceCardImage?.path
      : details?.kycBackImage?.path;

  if (isLoading) {
    return <StorePreviewSkeleton />;
  }
  return (
    <div className="w-full flex flex-col gap-6 p-5 max-h-[620px] overflow-auto">
      {/* CLUB INFO */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Label className="whitespace-nowrap font-bold">
            <Translation id="storeManagement.premiumStores.modal.clubDetails.clubInfo" />:
          </Label>
          <hr className="h-0.5 w-full text-border-subtle" />
        </div>

        <div className="flex gap-3.5 text-sm">
          <div className="w-[200px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.form.storeInfo.label.storeName" />:
          </div>
          <div className="font-bold">{details?.storeName || 'N/A'}</div>
        </div>

        <div className="flex gap-3.5 text-sm">
          <div className="w-[200px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.premiumStores.modal.clubDetails.storeNameKatagana" />:
          </div>
          <div className="font-bold">{details?.storeNameFurigana || 'N/A'}</div>
        </div>

        <div className="flex gap-3.5 text-sm">
          <div className="w-[200px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.basicStores.manageStoreInfo.clubAddress.postalCode" />:
          </div>
          <div className="font-bold">{details?.postalCode || 'N/A'}</div>
        </div>

        <div className="flex gap-3.5 text-sm">
          <div className="w-[200px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.form.storeInfo.label.prefecture" />:
          </div>
          <div className="font-bold">{details?.locationName || 'N/A'}</div>
        </div>

        <div className="flex gap-3.5 text-sm">
          <div className="w-[200px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.form.storeInfo.label.city" />:
          </div>
          <div className="font-bold">{details?.city || 'N/A'}</div>
        </div>

        <div className="flex gap-3.5 text-sm">
          <div className="w-[200px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.form.storeInfo.label.street" />:
          </div>
          <div className="font-bold">{details?.street || 'N/A'}</div>
        </div>

        <div className="flex gap-3.5 text-sm">
          <div className="w-[200px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.premiumStores.modal.clubDetails.buildingRoomNo" />:
          </div>
          <div className="font-bold">{details?.buildingNo || 'N/A'}</div>
        </div>

        <div className="flex gap-3.5 text-sm">
          <div className="w-[200px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.premiumStores.modal.clubDetails.landlineNumber" />:
          </div>
          <div className="font-bold">{details?.landlineNumber || 'N/A'}</div>
        </div>

        <div className="flex gap-3.5 text-sm">
          <div className="w-[200px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.form.storeInfo.label.groupName" />:
          </div>
          <div className="font-bold">{details?.groupName || 'N/A'}</div>
        </div>
      </div>

      {/* BUSINESS INFO */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Label className="whitespace-nowrap font-bold">
            <Translation id="storeManagement.premiumStores.modal.clubDetails.businessInfo" />:
          </Label>
          <hr className="h-0.5 w-full text-border-subtle" />
        </div>

        <div className="flex gap-2 text-sm">
          <div className="w-[236px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.premiumStores.modal.businessDetails.businessType" />:
          </div>
          <div className="font-bold">{getBusinessType(details?.businessType)}</div>
        </div>

        <div className="flex gap-2 text-sm">
          <div className="w-[236px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.form.businessInfo.label.ceoName" />:
          </div>
          <div className="font-bold">{details?.ceoFullName || 'N/A'}</div>
        </div>

        <div className="flex gap-2 text-sm">
          <div className="w-[236px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.form.storeInfo.label.furigana" />:
          </div>
          <div className="font-bold">{details?.ceoFurigana || 'N/A'}</div>
        </div>

        {details.businessType === '1' && (
          <>
            <div className="flex gap-2 text-sm">
              <div className="w-[236px] font-medium text-foreground-subdued">
                <Translation id="storeManagement.form.businessInfo.label.companyName" />:
              </div>
              <div className="font-bold">{details?.companyName || 'N/A'}</div>
            </div>
            <div className="flex gap-2 text-sm">
              <div className="w-[236px] font-medium text-foreground-subdued">
                <Translation id="storeManagement.form.businessInfo.label.companyNameFurigana" />:
              </div>
              <div className="font-bold">{details?.companyNameFurigana || 'N/A'}</div>
            </div>
          </>
        )}

        <div className="flex gap-2 text-sm">
          <div className="w-[236px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.premiumStores.modal.clubDetails.businessLicense" />:
          </div>
          <div className="font-bold text-foreground-subdued flex items-center">
            <GalleryIcon width={20} height={20} />
            License.jpg
            <span className="text-link-secondary ml-2 text-micro">
              <u>
                <button
                  type="button"
                  onClick={() => {
                    onOpen({
                      title: '',
                      okText: '  ',
                      cancelText: '',
                      size: 'img',
                      onCancel: () => {},
                      modalTitle: '',
                      component: (
                        <div className="flex items-center justify-center !w-full">
                          <div className="h-[420px] w-[310px]">
                            <img
                              src={details?.businessCertificateImage?.path}
                              alt="doc"
                              className="h-full w-full object-contain"
                            />
                          </div>
                        </div>
                      ),
                      image: true,
                    });
                  }}
                  className="underline cursor-pointer"
                >
                  View
                </button>
              </u>
            </span>
            <span className="text-link-secondary ml-2 text-micro">
              <button
                className="cursor-pointer"
                type="button"
                onClick={() => void downloadFromUrl(details?.businessCertificateImage?.path ?? '', 'license.jpg')}
              >
                <u>Download</u>
              </button>
            </span>
          </div>
        </div>

        <div className="flex gap-2 text-sm">
          <div className="w-[236px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.premiumStores.documents.identificationType" />:
          </div>
          <div className="font-bold">
            {getLabelFromAttributes(details?.identificationType, identificationTypeOptions as Attr[])}
          </div>
        </div>

        <div className="flex gap-2 text-sm">
          <div className="w-[236px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.premiumStores.modal.clubDetails.kycDocument" />:
          </div>

          <div>
            <div className="font-bold text-foreground-subdued flex items-center">
              <GalleryIcon width={20} height={20} />
              KYC1.jpg
              <span className="text-link-secondary ml-2 text-micro">
                <u>
                  <button
                    type="button"
                    onClick={() => {
                      onOpen({
                        title: '',
                        okText: '  ',
                        cancelText: '',
                        size: 'img',
                        onCancel: () => {},
                        modalTitle: '',
                        component: (
                          <div className="flex items-center justify-center !w-full">
                            <div className="h-[420px] w-[310px]">
                              <img src={kyc1Url} alt="doc" className="h-full w-full object-contain" />
                            </div>
                          </div>
                        ),
                        image: true,
                      });
                    }}
                    className="underline cursor-pointer"
                  >
                    View
                  </button>
                </u>
              </span>
              <span className="text-link-secondary ml-2 text-micro">
                <button
                  className="cursor-pointer"
                  type="button"
                  onClick={() => void downloadFromUrl(kyc1Url ?? '', 'KYC1.jpg')}
                >
                  <u>Download</u>
                </button>
              </span>
            </div>

            <div className="font-bold text-foreground-subdued flex items-center">
              <GalleryIcon width={20} height={20} />
              KYC2.jpg
              <span className="text-link-secondary ml-2 text-micro">
                <u>
                  <button
                    type="button"
                    onClick={() => {
                      onOpen({
                        title: '',
                        okText: '  ',
                        cancelText: '',
                        size: 'img',
                        onCancel: () => {},
                        modalTitle: '',
                        component: (
                          <div className="flex items-center justify-center !w-full">
                            <div className="h-[420px] w-[310px]">
                              <img src={kyc2Url} alt="doc" className="h-full w-full object-contain" />
                            </div>
                          </div>
                        ),
                        image: true,
                      });
                    }}
                    className="underline cursor-pointer"
                  >
                    View
                  </button>
                </u>
              </span>
              <span className="text-link-secondary ml-2 text-micro">
                <button
                  className="cursor-pointer"
                  type="button"
                  onClick={() => void downloadFromUrl(kyc2Url ?? '', 'KYC2.jpg')}
                >
                  <u>Download</u>
                </button>
              </span>
            </div>
          </div>
        </div>

        {!!details?.corporateRegistryImage?.path && (
          <div className="flex gap-2 text-sm">
            <div className="w-[236px] font-medium text-foreground-subdued">
              <Translation id="storeManagement.premiumStores.modal.documentType.corporateRegistry" />:
            </div>
            <div className="font-bold text-foreground-subdued flex items-center">
              <GalleryIcon width={20} height={20} />
              Registry.jpg
              <span className="text-link-secondary ml-2 text-micro">
                <u>
                  <button
                    type="button"
                    onClick={() => {
                      onOpen({
                        title: '',
                        okText: '  ',
                        cancelText: '',
                        size: 'img',
                        onCancel: () => {},
                        modalTitle: '',
                        component: (
                          <div className="flex items-center justify-center !w-full">
                            <div className="h-[420px] w-[310px]">
                              <img
                                src={details?.corporateRegistryImage?.path}
                                alt="doc"
                                className="h-full w-full object-contain"
                              />
                            </div>
                          </div>
                        ),
                        image: true,
                      });
                    }}
                    className="underline cursor-pointer"
                  >
                    View
                  </button>
                </u>
              </span>
              <span className="text-link-secondary ml-2 text-micro">
                <button
                  className="cursor-pointer"
                  type="button"
                  onClick={() => void downloadFromUrl(details?.corporateRegistryImage?.path ?? '', 'registry.jpg')}
                >
                  <u>Download</u>
                </button>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* USER INFO */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Label className="whitespace-nowrap font-bold">
            <Translation id="storeManagement.premiumStores.modal.clubDetails.userInfo" />:
          </Label>
          <hr className="h-0.5 w-full text-border-subtle" />
        </div>

        <div className="flex gap-1 text-sm">
          <div className="w-[236px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.premiumStores.modal.clubDetails.mobileNumber" />:
          </div>
          <div className="font-bold">{details?.mobileNumber || 'N/A'}</div>
        </div>

        <div className="flex gap-1 text-sm">
          <div className="w-[236px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.form.businessInfo.label.emailAddress" />:
          </div>
          <div className="font-bold">{details?.emailAddress || 'N/A'}</div>
        </div>

        <div className="flex gap-1 text-sm">
          <div className="w-[236px] font-medium text-foreground-subdued">
            <Translation id="storeManagement.premiumStores.modal.clubDetails.clubUsername" />:
          </div>
          <div className="font-bold">{details?.username || 'N/A'}</div>
        </div>
      </div>
    </div>
  );
};

export default StorePreview;
